import { IpfError } from '@ipfabric/frontend-shared-assets/utils/IpfError';
import { TooltipIcon } from '@ipfabric/ui-components/Tooltip/TooltipIcon';
import {
  type LoadingState,
  withLoadingIndicator,
} from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import type {
  GraphOverlay,
  GraphPathLookupParameters,
  PathLookupPassingTraffic,
} from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { Button } from 'components/Button/Button';
import { ButtonAsA } from 'components/Button/ButtonAsA';
import { FormikDoublePoleSwitch } from 'components/FormikDoublePoleSwitchField/FormikDoublePoleSwitch';
import { useGraphContextSelectors } from 'components/Graph/GraphContext';
import {
  type PathLookupGraphMetadata,
  isPathLookupGraphMetadata,
} from 'components/Graph/GraphState';
import { fetchPathLookupCheckInitialExpectedPassingTraffic } from 'components/Graph/commands/fetchPathLookupCheckInitialExpectedPassingTraffic';
import { savePathLookupCheck } from 'components/Graph/commands/savePathLookupCheck';
import { showSidePanel } from 'components/Graph/commands/showSidePanel';
import { useLocale } from 'containers/App/LocaleContext';
import { type FormikErrors, useFormik } from 'formik';
import { memo, useEffect, useState } from 'react';
import type { FC, MouseEvent } from 'react';
import type { AppTranslations } from 'translations/translations';
import { makeFormikOnSubmit } from 'utils/formik/makeFormikOnSubmit';
import { PathDetectedState } from './PathDetectedState';

interface Props {
  type: 'unicast' | 'multicast';
}

export interface PathLookupCheckFormValues {
  expectedPassingTraffic: PathLookupPassingTraffic | null;
}

const getBtnContent = (
  savingState: LoadingState,
  initialExpectedPassingTraffic: PathLookupPassingTraffic | null,
  expectedPassingTrafficFormValue: PathLookupCheckFormValues['expectedPassingTraffic'],
  checkingExists: boolean,
  msg: AppTranslations,
) => {
  if (checkingExists) {
    return (
      <>
        <i className='fa-regular fa-spinner fa-spin mr-lgu-2' />
        {
          msg.canvasGraph.graphSidePanel.pathlookup.pathVerification.form
            .btnChecking
        }
      </>
    );
  }
  if (savingState === 'LOADING') {
    return (
      <>
        <i className='fa-regular fa-spinner fa-spin mr-lgu-2' />
        {
          msg.canvasGraph.graphSidePanel.pathlookup.pathVerification.form
            .btnSaving
        }
      </>
    );
  }
  if (
    initialExpectedPassingTraffic === expectedPassingTrafficFormValue &&
    expectedPassingTrafficFormValue !== null
  ) {
    return (
      <>
        <i className='fa fa-check mr-lgu-2' />
        {
          msg.canvasGraph.graphSidePanel.pathlookup.pathVerification.form
            .btnSaved
        }
      </>
    );
  }
  return msg.canvasGraph.graphSidePanel.pathlookup.pathVerification.form
    .btnSave;
};

const getDisabledSubmitExplanationMessage = (
  msg: AppTranslations,
  overlay: GraphOverlay | null,
  appliedPathLookupOfCurrentType: PathLookupGraphMetadata | null,
): string | null => {
  if (overlay?.type === 'intent') {
    return msg.canvasGraph.graphSidePanel.pathlookup.pathVerification
      .appliedIntentCheckOverlay;
  }
  if (overlay?.type === 'compare') {
    return msg.canvasGraph.graphSidePanel.pathlookup.pathVerification
      .appliedCompareOverlay;
  }
  if (appliedPathLookupOfCurrentType === null) {
    return msg.canvasGraph.graphSidePanel.pathlookup.pathVerification
      .notSubmittedPathLookup;
  }
  return null;
};

const BarePathCheck: FC<Props> = (props) => {
  const msg = useLocale();
  const [checkingExists, setCheckingExists] = useState<boolean>(false);
  const [savingState, setSavingState] = useState<LoadingState>('INITIAL');
  const ctxProps = useGraphContextSelectors({
    appliedPathLookupOfCurrentType: (s) =>
      isPathLookupGraphMetadata(s.graphMetadata) &&
      s.graphMetadata.parameters.pathLookupType === props.type
        ? s.graphMetadata
        : null,
    dispatch: prop('dispatch'),
    overlay: prop('overlay'),
  });

  const { initialExpectedPassingTraffic, passingTraffic } =
    (ctxProps.overlay === null
      ? ctxProps.appliedPathLookupOfCurrentType
      : null) ?? {
      initialExpectedPassingTraffic: null,
      passingTraffic: null,
    };

  const handleSubmit = (values: PathLookupCheckFormValues) => {
    withLoadingIndicator(setSavingState)(async () => {
      if (!ctxProps.appliedPathLookupOfCurrentType) {
        throw new IpfError(
          'INVALID_ARGUMENT',
          `Trying to save ${props.type} path check while none applied`,
        );
      }
      await ctxProps.dispatch(savePathLookupCheck, {
        parameters: ctxProps.appliedPathLookupOfCurrentType.parameters,
        values,
      });
    });
  };

  const handleSeeAllSavedChecks = () => {
    ctxProps.dispatch(showSidePanel, {
      intentVerificationTabId: 'path-checks',
    });
  };

  const formik = useFormik({
    initialValues: {
      expectedPassingTraffic: initialExpectedPassingTraffic,
    },
    enableReinitialize: true,
    validate: (values) => {
      const errors: FormikErrors<PathLookupCheckFormValues> = {};

      if (values.expectedPassingTraffic === null) {
        errors.expectedPassingTraffic = 'errorExpectedState';
      }

      return errors;
    },
    onSubmit: makeFormikOnSubmit(handleSubmit),
  });

  const handleSave = (e: MouseEvent) => {
    e.preventDefault();

    formik.handleSubmit();
  };

  const checkExists = async (parameters: GraphPathLookupParameters) => {
    setCheckingExists(true);
    try {
      await ctxProps.dispatch(
        fetchPathLookupCheckInitialExpectedPassingTraffic,
        { parameters },
      );
    } finally {
      setCheckingExists(false);
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: toggle on expected passing traffic changes
  useEffect(() => {
    if (ctxProps.appliedPathLookupOfCurrentType) {
      checkExists(ctxProps.appliedPathLookupOfCurrentType.parameters);
    }
  }, [formik.values.expectedPassingTraffic]);

  const disabledSubmitExplanationMessage = getDisabledSubmitExplanationMessage(
    msg,
    ctxProps.overlay,
    ctxProps.appliedPathLookupOfCurrentType,
  );

  const pathChecksNotAvailable =
    ctxProps.appliedPathLookupOfCurrentType === null ||
    ctxProps.overlay !== null;

  return (
    <div className='graph-pathlookup__path-check py-lgu-10 mx-5'>
      <span className='graph-pathlookup__section-label'>
        {msg.canvasGraph.graphSidePanel.pathlookup.pathVerification.title}
        <TooltipIcon
          content={
            msg.canvasGraph.graphSidePanel.pathlookup.pathVerification.tooltip
          }
          className='ml-2'
          iconProps={{
            iconStyle: 'solid',
            inline: true,
          }}
          size='sm'
        />
      </span>
      <form className='flex items-end justify-between'>
        <div className='mt-5'>
          <label className='ipf-form-label' htmlFor={undefined}>
            {
              msg.canvasGraph.graphSidePanel.pathlookup.pathVerification.form
                .intendedState
            }
          </label>
          <div className='flex h-10 items-center'>
            <FormikDoublePoleSwitch
              disabled={pathChecksNotAvailable || checkingExists}
              formik={formik}
              left={{
                label:
                  msg.canvasGraph.graphSidePanel.pathlookup.pathVerification
                    .form.pass,
                value: 'all',
              }}
              name='expectedPassingTraffic'
              right={{
                label:
                  msg.canvasGraph.graphSidePanel.pathlookup.pathVerification
                    .form.fail,
                value: 'none',
              }}
            />
          </div>
        </div>
        <div>
          <label className='ipf-form-label' htmlFor={undefined}>
            {
              msg.canvasGraph.graphSidePanel.pathlookup.pathVerification.form
                .detectedState
            }
          </label>
          <div className='flex h-10 items-center'>
            <PathDetectedState
              passingTraffic={passingTraffic}
              expectedPassingTraffic={formik.values.expectedPassingTraffic}
            />
          </div>
        </div>
        <Button
          disabled={
            formik.values.expectedPassingTraffic === null ||
            checkingExists ||
            pathChecksNotAvailable ||
            savingState === 'LOADING' ||
            formik.values.expectedPassingTraffic ===
              initialExpectedPassingTraffic
          }
          className='px-lgu-1 w-2.58 mb-1'
          onClick={handleSave}
          size='xs'
          type='submit'
          variant='secondary'
        >
          {getBtnContent(
            savingState,
            initialExpectedPassingTraffic,
            formik.values.expectedPassingTraffic,
            checkingExists,
            msg,
          )}
        </Button>
      </form>
      {savingState === 'ERROR' && (
        <p className='text-danger mt-lgu-2 text-right'>
          {msg.canvasGraph.graphSidePanel.pathlookup.pathVerification.error}
        </p>
      )}
      {disabledSubmitExplanationMessage !== null && (
        <div className='graph-pathlookup__disabled-checks-explanation mt-lgu-6 flex font-bold'>
          <i className='fa fa-fw fa-exclamation-circle leading-lgu-5 mt-lgu-1 mr-lgu-2' />
          <span>{disabledSubmitExplanationMessage}</span>
        </div>
      )}
      <div className='mt-lgu-6 flex justify-end'>
        <ButtonAsA
          className='graph-pathlookup__see-all-checks font-bold'
          onClick={handleSeeAllSavedChecks}
        >
          {msg.canvasGraph.graphSidePanel.pathlookup.pathVerification.seeSaved}
        </ButtonAsA>
      </div>
    </div>
  );
};

export const PathCheck = memo(BarePathCheck);
